import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../store/authStore';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

export function LoginPage() {
  const { t } = useTranslation();
  const { login } = useAuthStore();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      navigate('/');
    } catch (err) {
      setError(t('auth.loginError'));
    }
    setLoading(false);
  };

  return (
    <div className="min-h-[60vh] flex items-center justify-center px-4" data-testid="login-page">
      <div className="bg-white rounded-2xl shadow-sm p-8 w-full max-w-md">
        <h1 className="font-serif text-3xl font-bold text-primary text-center mb-8">{t('auth.loginTitle')}</h1>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-secondary mb-1">{t('auth.email')}</label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email" />
          </div>
          <div>
            <label className="block text-sm font-medium text-secondary mb-1">{t('auth.password')}</label>
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password" />
          </div>
          {error && <p className="text-red-500 text-sm">{error}</p>}
          <Button type="submit" disabled={loading} className="w-full bg-gold hover:bg-gold/90 text-white" style={{ backgroundColor: '#D4A574' }} data-testid="login-submit">
            {loading ? t('common.loading') : t('auth.loginButton')}
          </Button>
        </form>

        <p className="text-center text-secondary mt-6">
          {t('auth.noAccount')} <Link to="/register" className="text-gold hover:underline" style={{ color: '#D4A574' }}>{t('nav.register')}</Link>
        </p>
      </div>
    </div>
  );
}

export function RegisterPage() {
  const { t, i18n } = useTranslation();
  const { register } = useAuthStore();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ email: '', password: '', name: '', language: i18n.language });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await register(formData.email, formData.password, formData.name, formData.language);
      navigate('/');
    } catch (err) {
      setError(t('auth.registerError'));
    }
    setLoading(false);
  };

  return (
    <div className="min-h-[60vh] flex items-center justify-center px-4" data-testid="register-page">
      <div className="bg-white rounded-2xl shadow-sm p-8 w-full max-w-md">
        <h1 className="font-serif text-3xl font-bold text-primary text-center mb-8">{t('auth.registerTitle')}</h1>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-secondary mb-1">{t('auth.name')}</label>
            <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required data-testid="register-name" />
          </div>
          <div>
            <label className="block text-sm font-medium text-secondary mb-1">{t('auth.email')}</label>
            <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required data-testid="register-email" />
          </div>
          <div>
            <label className="block text-sm font-medium text-secondary mb-1">{t('auth.password')}</label>
            <Input type="password" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} required data-testid="register-password" />
          </div>
          <div>
            <label className="block text-sm font-medium text-secondary mb-1">{t('profile.language')}</label>
            <Select value={formData.language} onValueChange={(v) => setFormData({ ...formData, language: v })}>
              <SelectTrigger data-testid="register-language"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="es">Espanol</SelectItem>
                <SelectItem value="en">English</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {error && <p className="text-red-500 text-sm">{error}</p>}
          <Button type="submit" disabled={loading} className="w-full bg-gold hover:bg-gold/90 text-white" style={{ backgroundColor: '#D4A574' }} data-testid="register-submit">
            {loading ? t('common.loading') : t('auth.registerButton')}
          </Button>
        </form>

        <p className="text-center text-secondary mt-6">
          {t('auth.hasAccount')} <Link to="/login" className="text-gold hover:underline" style={{ color: '#D4A574' }}>{t('nav.login')}</Link>
        </p>
      </div>
    </div>
  );
}
